import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography, useTheme } from '@mui/material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import yaml from 'js-yaml';
import { config } from '../../config/config';
import { applyPathModuleTags } from './openapiModuleTags';
import { getApiDocsSwaggerSx } from './apiDocsSwaggerStyles';

const apiBaseUrl = config.api.baseUrl.replace(/\/+$/, '');

function setAuthHeader(headers: unknown, token: string): void {
    if (!headers) return;
    if (typeof (headers as Headers).set === 'function') {
        (headers as Headers).set('Authorization', `Bearer ${token}`);
        return;
    }
    const h = headers as Record<string, string>;
    h.Authorization = `Bearer ${token}`;
}

const ApiDocsPage: React.FC = () => {
    const theme = useTheme();
    const swaggerHostSx = useMemo(() => getApiDocsSwaggerSx(theme), [theme]);

    const [spec, setSpec] = useState<Record<string, unknown> | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setLoadError('You must be signed in to load the API specification.');
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`${apiBaseUrl}/openapi.yaml`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!res.ok) {
                    if (!cancelled) {
                        setLoadError(
                            res.status === 401
                                ? 'Session expired or unauthorized.'
                                : `Could not load API specification (HTTP ${res.status}).`
                        );
                    }
                    return;
                }

                const text = await res.text();
                const parsed = yaml.load(text) as Record<string, unknown>;
                if (cancelled) return;

                parsed.servers = [{ url: apiBaseUrl, description: 'API (from app configuration)' }];
                applyPathModuleTags(parsed);
                setSpec(parsed);
            } catch {
                if (!cancelled) {
                    setLoadError('Failed to load or parse the OpenAPI specification.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, []);

    const requestInterceptor = useCallback((req: { headers?: unknown }) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            setAuthHeader(req.headers, token);
        }
        return req;
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    if (!spec) {
        return <Alert severity="warning">No specification was loaded.</Alert>;
    }

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, pb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5, letterSpacing: '-0.02em' }}>
                API reference
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 'none' }}>
                OpenAPI for this environment. Filter by keyword or browse by section. Setup-style resources from{' '}
                <code>/masterdata</code> appear under short names (Purposes, Data elements, …) with a{' '}
                <strong>Master data</strong> label in each heading.
            </Typography>

            <Alert severity="info" variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
                Try it is read-only: only <strong>GET</strong> and <strong>HEAD</strong> can be executed. Other methods
                are shown for documentation only.
            </Alert>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    overflow: 'hidden',
                    bgcolor: 'background.paper'
                }}
            >
                <Box className="swagger-docs-host" sx={swaggerHostSx}>
                    <SwaggerUI
                        spec={spec}
                        supportedSubmitMethods={['get', 'head']}
                        requestInterceptor={requestInterceptor}
                        deepLinking
                        docExpansion="none"
                        defaultModelExpandDepth={1}
                        defaultModelsExpandDepth={1}
                        filter
                        persistAuthorization
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default ApiDocsPage;
